import {
  PaintbrushIcon,
  SprayCanIcon,
  LayersIcon,
  SquareStackIcon,
  LayoutGridIcon,
  SofaIcon,
  HammerIcon,
  Trash2Icon,
} from 'lucide-react'
import type { Service } from '../types/content'

export const services: Service[] = [
  {
    slug: 'interior-painting',
    icon: PaintbrushIcon,
    name: { en: 'Interior Painting', es: 'Pintura Interior' },
    short: {
      en: 'Walls, ceilings, trim, doors and cabinets.',
      es: 'Paredes, techos, molduras, puertas y gabinetes.',
    },
    description: {
      en: 'Clean lines, full protection of your floors and furniture, and premium paint that holds its finish. We handle everything from a single accent wall to a whole-home repaint, including cabinet refinishing.',
      es: 'Líneas limpias, protección total de tus pisos y muebles, y pintura premium que mantiene su acabado. Hacemos desde una pared decorativa hasta la casa completa, incluyendo gabinetes.',
    },
    bullets: [
      { en: 'Walls, ceilings, trim and baseboards', es: 'Paredes, techos, molduras y zócalos' },
      { en: 'Door and cabinet refinishing', es: 'Renovación de puertas y gabinetes' },
      { en: 'Premium paint selection', es: 'Selección de pintura premium' },
      { en: 'Professional color consultation', es: 'Asesoría profesional de color' },
    ],
  },
  {
    slug: 'exterior-painting',
    icon: SprayCanIcon,
    name: { en: 'Exterior Painting', es: 'Pintura Exterior' },
    short: {
      en: 'Facades, eaves, doors and window frames.',
      es: 'Fachadas, techos, puertas y ventanas.',
    },
    description: {
      en: 'Texas sun and storms are hard on a house. We prep, seal and coat with weather-rated products so your exterior keeps its color for years, not seasons.',
      es: 'El sol y las tormentas de Texas castigan una casa. Preparamos, sellamos y aplicamos productos resistentes al clima para que el exterior conserve su color por años.',
    },
    bullets: [
      { en: 'Pressure washing and surface prep', es: 'Lavado a presión y preparación' },
      { en: 'Caulking and rot repair', es: 'Sellado y reparación de madera' },
      { en: 'Stucco, siding and brick', es: 'Estuco, siding y ladrillo' },
      { en: 'Weather-rated premium coatings', es: 'Recubrimientos premium para clima' },
    ],
  },
  {
    slug: 'drywall',
    icon: LayersIcon,
    name: { en: 'Drywall Installation & Repair', es: 'Instalación y Reparación de Drywall' },
    short: {
      en: 'Tape, float and texture — invisible repairs.',
      es: 'Cinta, masilla y textura — reparaciones invisibles.',
    },
    description: {
      en: 'From full sheet installation to patching water damage and holes. We match any existing texture — orange peel, knockdown, smooth — so the repair disappears into the wall.',
      es: 'Desde instalación completa hasta parches por daños de agua y hoyos. Igualamos cualquier textura existente — orange peel, knockdown o lisa — para que la reparación desaparezca.',
    },
    bullets: [
      { en: 'New installation and framing-out', es: 'Instalación nueva y enmarcado' },
      { en: 'Water damage and hole repair', es: 'Reparación de hoyos y daños de agua' },
      { en: 'Tape, float and professional finishing', es: 'Cinta, masilla y acabado profesional' },
      { en: 'All textures matched', es: 'Todas las texturas igualadas' },
    ],
  },
  {
    slug: 'laminate-flooring',
    icon: SquareStackIcon,
    name: { en: 'Laminated Flooring', es: 'Pisos Laminados' },
    short: {
      en: 'Premium-quality laminate, precisely installed.',
      es: 'Pisos laminados de calidad premium.',
    },
    description: {
      en: 'Level subfloor prep, tight seams and clean transitions at every doorway. We install the underlayment and trim correctly so the floor stays quiet and flat.',
      es: 'Preparación nivelada del subpiso, uniones ajustadas y transiciones limpias en cada puerta. Instalamos la base y las molduras correctamente para un piso firme y silencioso.',
    },
    bullets: [
      { en: 'Subfloor leveling and moisture barrier', es: 'Nivelación y barrera de humedad' },
      { en: 'Precision cuts and tight seams', es: 'Cortes precisos y uniones ajustadas' },
      { en: 'Baseboard and transition trim', es: 'Zócalos y molduras de transición' },
      { en: 'Old flooring hauled away', es: 'Retiro del piso anterior' },
    ],
  },
  {
    slug: 'lvp-flooring',
    icon: LayoutGridIcon,
    name: { en: 'LVP Luxury Vinyl Plank', es: 'Pisos LVP de Vinil' },
    short: {
      en: 'Modern, waterproof and built to last.',
      es: 'Pisos modernos, duraderos y resistentes al agua.',
    },
    description: {
      en: 'The best value for Dallas homes with kids and pets: waterproof, scratch-resistant and warm underfoot. Dozens of wood looks in wide-plank formats.',
      es: 'La mejor opción para casas en Dallas con niños y mascotas: resistente al agua y a rayones. Docenas de acabados de madera en formato de tabla ancha.',
    },
    bullets: [
      { en: 'Waterproof, pet and kid friendly', es: 'Resistente al agua, niños y mascotas' },
      { en: 'Wide-plank and tile-look options', es: 'Opciones de tabla ancha y tipo loseta' },
      { en: 'Kitchens, baths and full homes', es: 'Cocinas, baños y casas completas' },
      { en: 'Quality materials, quality install', es: 'Materiales y mano de obra de calidad' },
    ],
  },
  {
    slug: 'carpeting',
    icon: SofaIcon,
    name: { en: 'Carpeting Install & Removal', es: 'Instalación y Retiro de Alfombra' },
    short: {
      en: 'Quality carpet, stretched and finished right.',
      es: 'Alfombras de calidad, bien estiradas y terminadas.',
    },
    description: {
      en: 'New carpet and pad for bedrooms, stairs and offices — or complete removal and disposal if you are moving to hard surface flooring.',
      es: 'Alfombra y base nuevas para recámaras, escaleras y oficinas — o retiro completo si prefieres cambiar a piso duro.',
    },
    bullets: [
      { en: 'Bedrooms, stairs and offices', es: 'Recámaras, escaleras y oficinas' },
      { en: 'New pad and power stretching', es: 'Base nueva y estirado profesional' },
      { en: 'Full removal and disposal', es: 'Retiro y desecho completo' },
      { en: 'Pet odor and subfloor treatment', es: 'Tratamiento de olores y subpiso' },
    ],
  },
  {
    slug: 'remodeling',
    icon: HammerIcon,
    name: { en: 'Structure Building & Remodeling', es: 'Construcción y Remodelación' },
    short: {
      en: 'Framing, room additions and full renovations.',
      es: 'Framing, ampliaciones y renovaciones.',
    },
    description: {
      en: 'Kitchens, bathrooms, room additions and framing. One crew from demolition to final paint, so nothing gets lost between trades.',
      es: 'Cocinas, baños, ampliaciones y framing. Un solo equipo desde la demolición hasta la pintura final, sin perder nada entre oficios.',
    },
    bullets: [
      { en: 'Structure building and framing', es: 'Construcción de estructuras y framing' },
      { en: 'Kitchen and bathroom renovations', es: 'Renovación de cocinas y baños' },
      { en: 'Room additions (ampliaciones)', es: 'Ampliaciones de habitaciones' },
      { en: 'Design consultation included', es: 'Asesoría de diseño incluida' },
    ],
  },
  {
    slug: 'demolition-junk',
    icon: Trash2Icon,
    name: { en: 'Demolition & Junk Removal', es: 'Demolición y Retiro de Basura' },
    short: {
      en: 'Demo, cleanout and dumpster service.',
      es: 'Demoliciones, limpieza y servicio de dumpster.',
    },
    description: {
      en: 'Interior demo, garage and property cleanouts, plus dumpster drop-off and haul-away. We leave the site broom-clean and ready for the next phase.',
      es: 'Demolición interior, limpieza de garajes y propiedades, más servicio de dumpster y retiro. Dejamos el sitio limpio y listo para la siguiente etapa.',
    },
    bullets: [
      { en: 'Interior and selective demolition', es: 'Demolición interior y selectiva' },
      { en: 'Property and garage cleanouts', es: 'Limpieza de propiedades y garajes' },
      { en: 'Dumpster drop-off and pickup', es: 'Entrega y recolección de dumpster' },
      { en: 'Broom-clean site handover', es: 'Entrega del sitio completamente limpio' },
    ],
  },
]
